import { Pause, Play, Volume2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/button";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { Form } from "@/features/request/feedback-tool/feedbackTool/audio/components/Form.tsx";
import { useCanAddMoreFeedbacks } from "@/features/request/feedback-tool/feedbackTool/hooks/useCanAddMoreFeedbacks";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";

export function Player({ media }: { media: string | undefined }) {
  const {
    audioRef,
    feedback: feedBack,
    formFeedBack,
    img,
    selected,
    setFormFeedBack,
    setSelected,
  } = useFeedbackTool();
  const timelineRef = useRef<HTMLDivElement>(null);
  const { hasRole } = useRoleAccess();
  const isDesigner = hasRole(["designer"]);
  const canNotAddFeedback = useCanAddMoreFeedbacks();
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [aCurrentTime, setACurrentTime] = useState(0);
  const [aDuration, setADuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;
    const timestamp = selected?.timestamp;

    if (typeof timestamp === "number" && !Number.isNaN(timestamp)) {
      audio.currentTime = timestamp;
      audio.pause();
      setIsPlaying(false);
    }
  }, [selected]);

  useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      audio.currentTime = 0;
      setFormFeedBack(null);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    const updateProgress = () => {
      setACurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const setAudioDuration = () => {
      setADuration(audio.duration);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", setAudioDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", setAudioDuration);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;

    if (!audio) return;

    if (audio.paused) {
      audio.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isDesigner || canNotAddFeedback) return;

    const audio = audioRef.current;

    if (!audio || !timelineRef.current) return;
    if (selected) return setSelected(null);

    const rect = timelineRef.current.getBoundingClientRect();
    const clickPercent = Math.min(
      100,
      Math.max(0, ((event.clientX - rect.left) / rect.width) * 100),
    );
    const newTime = (clickPercent / 100) * audio.duration;

    audio.currentTime = newTime;
    audio.pause();
    setIsPlaying(false);
    setProgress(clickPercent);

    setFormFeedBack({
      description: "",
      format: "AUDIO",
      id: feedBack ? feedBack.length + 1 : 1,
      imgId: img.id,
      storyId: 0,
      timestamp: newTime,
      type: "ADD",
      user: {
        id: 0,
        name: "",
      },
      x: 0,
      y: 0,
    });
  };

  return (
    <div className="w-full px-4">
      <audio ref={audioRef} src={media} style={{ display: "none" }}>
        You browser don&apos;t support the audio tag.
      </audio>
      <div className="flex flex-col gap-1 mt-3 mb-3">
        <div className="relative w-full h-2">
          <div
            className="absolute inset-0 h-2 rounded-lg bg-slate-300 cursor-crosshair"
            onClick={handleTimelineClick}
            ref={timelineRef}
          >
            <div
              className="h-full rounded-lg bg-fg-brand-primary pointer-events-none"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="absolute -top-[16px] left-0 w-full h-2 pointer-events-none">
            {feedBack?.map((item) => {
              const percentage =
                item?.timestamp && aDuration
                  ? (item.timestamp / aDuration) * 100
                  : 0;

              return (
                <Button
                  className={`
                   pointer-events-auto absolute bg-white w-5 h-7 flex justify-center items-center rounded-full text-black cursor-pointer border-slate-500
                   ${item.id === selected?.id ? "bg-button-primary-bg text-white" : "bg-white text-black"} z-[2]
                  `}
                  key={item.id}
                  onPress={() => {
                    if (formFeedBack) setFormFeedBack(null);
                    if (item === selected) {
                      setSelected(null);
                    } else {
                      setSelected(item);
                    }
                  }}
                  style={{
                    left: `calc(${percentage}% - 17px)`,
                  }}
                >
                  {item.id}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="flex mt-3 items-center justify-center">
          <button className="cursor-pointer" onClick={togglePlay} type="button">
            {isPlaying ? (
              <Pause className="text-slate-500" size={22} />
            ) : (
              <Play className="text-slate-500" size={22} />
            )}
          </button>
          <span className="ml-5 text-slate-500">
            {formatTime(aCurrentTime)} / {formatTime(aDuration)}
          </span>
          <Volume2 className="text-slate-500 ml-auto mr-2" size={18} />
          <input
            className="mr-4 w-24 h-1.5 rounded-lg accent-slate-500 cursor-pointer"
            max={1}
            min={0}
            onChange={(e) => {
              if (audioRef.current) {
                audioRef.current.volume = Number.parseFloat(e.target.value);
              }
            }}
            step={0.01}
            type="range"
          />
        </div>
      </div>
      <Form />
    </div>
  );
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}
